import { useMutation, useQueryClient } from '@tanstack/react-query';
import { assertAffectedRecord, queryKeys, sdk } from 'utils/query';
import { GeneralConfiguration, UserConfiguration, rqlBuilder } from '@extrahorizon/javascript-sdk';
import { snakeCaseKeys } from 'utils/helpers';
import { DocumentToApprove, DocumentToApproveConfiguration, DocumentsToApprove } from 'models/general';
import { displayNotificationSuccess } from 'utils/notifications';
import { useTranslation } from 'react-i18next';
import { GroupPatientConfiguration } from 'models/groups';
import { getMe } from './user';
import { EmptyParametersError } from './errors/EmptyParametersError';

export const getUserConfigurationById = async (userId: string) => {
  return await sdk.configurations.users.get(userId);
};

export const getGroupConfigurationById = async (groupId: string) => {
  return await sdk.configurations.groups.get(groupId);
};

export const getCurrentUserConfiguration = async () => {
  const user = await getMe();
  // Temporary fix because if hiddenGroups field is created by accident (can be removed in next versions)
  await sdk.configurations.users.removeFields(user.id || '', { fields: ['data.hiddenGroups'] });
  const configuration = await getUserConfigurationById(user.id || '');

  return configuration;
};

export const getGroupConfiguration = async (groupId: string) => {
  const configuration = await getGroupConfigurationById(groupId);

  if (!configuration.patientConfiguration) {
    const rql = rqlBuilder().eq('groupId', groupId);

    await sdk.users.groupRoles.addPermissions(
      groupId,
      {
        permissions: ['VIEW_PATIENT_CONFIGURATIONS', 'UPDATE_PATIENT_CONFIGURATIONS'],
      },
      { rql: rql.build() },
    );
    return await getGroupConfigurationById(groupId);
  }

  return configuration;
};

export const updateGroupConfiguration = async (variables: {
  id?: string;
  patientConfiguration?: GroupPatientConfiguration;
}) => {
  const { id, patientConfiguration } = variables;
  if (!id || !patientConfiguration) throw new EmptyParametersError();

  const result = await sdk.configurations.groups.update(id, { patientConfiguration });

  return assertAffectedRecord({ result });
};

export const getDocumentToApprove = (
  userConfiguration: UserConfiguration,
  generalConfiguration: GeneralConfiguration,
) => {
  const allDocuments = snakeCaseKeys(generalConfiguration?.data?.documents);
  const approvedDocuments = snakeCaseKeys(userConfiguration?.data?.documents);

  if (!allDocuments) return [];

  const keysListedInGeneral = Object.values(DocumentsToApprove).filter((key) =>
    Object.keys(allDocuments).includes(key),
  );

  const documentsToApprove: DocumentToApprove[] = keysListedInGeneral.map((key) => {
    const document: DocumentToApproveConfiguration = allDocuments[key];
    const { version, url } = document;
    let newDocument = true;
    let hasApproved = false;
    if (approvedDocuments) {
      newDocument = !Object.keys(approvedDocuments).includes(key);

      if (!newDocument) {
        hasApproved = Object.keys(approvedDocuments[key]).includes(`v${version.replaceAll('.', '')}`);
      }
    }

    return { key, isNew: newDocument, version, hasApproved, url };
  });

  return documentsToApprove;
};

const approveDocuments = async (variables: { userId: string; document: DocumentToApprove }) => {
  const { userId, document } = variables;
  const version = `v${document.version.replaceAll('.', '_')}`;

  const result = await sdk.configurations.users.update(userId, {
    data: { documents: { [document.key]: { [version]: { timestamp: new Date().toISOString() } } } },
  });

  return assertAffectedRecord({ result });
};

export const useApproveDocument = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  return useMutation({
    mutationFn: approveDocuments,
    onSuccess: async () => {
      displayNotificationSuccess({ message: t('document_approved_label') });
      await queryClient.invalidateQueries({ queryKey: [queryKeys.userData] });
    },
  });
};
